/**
 * forecast/weighting.ts — Phase 05.3 Plan 03; moved to @saledash/shared by quick-260922-mvc so the
 * sync-worker's weekly workbook archive and the SPA weight every dollar with the SAME functions.
 * spa-cro/src/lib/forecast-weighting.ts is now a re-export shim over this module.
 *
 * THE single source of truth for every dollar the Combined Revenue Forecast tab
 * renders. The equation strip, the year-to-date box, the deal tables and the
 * exported workbook all derive from these functions, which is what stops the
 * page contradicting itself.
 *
 * WHY THIS EXISTS AT ALL (05.3-RESEARCH Finding 1): the server-weighted fields
 * on the payload — `weighted_total`, `sub_groups[].weighted_total`,
 * `breakdown[].weighted_amount` — are baked at recompute time against the
 * STORED conversion grid. They cannot move with a per-viewer toggle. So nothing
 * in this module may read them: every figure is re-derived from `deals[]` via
 * `raw_amount x rate()`.
 *
 * WHY THE IMPORT BELOW IS A TYPE-ONLY IMPORT (and must stay that way):
 * importing a RUNTIME value from the `@saledash/shared` top barrel drags the
 * barrel's Node-only graph (`node:url`, `node:crypto`, `node:fs`) into the SPA
 * Rollup browser bundle and the build fails with "fileURLToPath is not exported
 * by __vite-browser-external". `lib/forecast-periods.ts` documents this in
 * full. Constants this module needs are declared locally and typed against the
 * shared union so they cannot silently drift from
 * packages/shared/src/forecast/schemas.ts.
 *
 * D-03: this module is PURE. No storage API, no memoisation, no snapshot. The
 * Upside % is a per-viewer viewing lens that is never written back
 * (T-05.3-03-03).
 */
import type { ForecastDeal, ForecastPosition, ForecastYtdQuarter } from './schemas.js';

// ---------------------------------------------------------------------------
// Position + sub-group labels, typed against the shared unions so they cannot
// drift from the server contract without a typecheck failure.
// ---------------------------------------------------------------------------

const COMMIT: ForecastPosition = 'Commit';
const MOST_LIKELY: ForecastPosition = 'Most Likely';
const BEST_CASE: ForecastPosition = 'Best Case';

/**
 * Closed Won is NOT a `Forecast_Position__c` value — 05.1 models it as the
 * separate `tier: 'closed_won'` and as the `ForecastPosition | 'Closed Won'`
 * breakdown label. Both spellings are recognised here so a payload carrying
 * only one of them still banks at 1.00.
 */
const CLOSED_WON_LABEL = 'Closed Won';

/**
 * D-13c: the ARR sub-groups. Managed Services is its own stream, New is New,
 * and EVERYTHING ELSE is Expansion (see `streams`).
 */
const SUB_GROUP_NEW = 'New';
const SUB_GROUP_MANAGED = 'Managed Services';

/**
 * D-02 / D-03: the stored Best Case weight after migration 058, and therefore
 * the toggle start position. 50 means "count half of Best Case".
 */
export const DEFAULT_UPSIDE_PCT = 50;

/**
 * Bound the one new user-controlled input on the page to 0..100 (T-05.3-03-01).
 *
 * Non-finite input maps to the default rather than propagating NaN into every
 * dollar figure on the tab. The value has no injection surface — it is never
 * interpolated into SQL, a URL or a request param, it only scales a
 * multiplication — so bounding plus the NaN guard is the whole control.
 */
export function clampUpsidePct(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_UPSIDE_PCT;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}

/**
 * D-04 — the weight a single deal carries at the viewer Upside %.
 *
 * Closed-Won banks at 1.00. Commit and Most Likely are fixed at 1.00. Best Case
 * is the ONLY position the toggle scales. Everything else — Pipeline, the
 * server-normalised 'No position' label, a blank string, and any position
 * string this module does not recognise — is 0.00.
 *
 * THE ZERO RETURN AT THE END IS DELIBERATE AND LOAD-BEARING (05.1 D-22).
 * A lookup table with a fallback weight, or any coercion of an unknown position
 * into a known bucket, is exactly the mechanism RESEARCH Pitfall 1 names as
 * silently inflating the forecast — a real blank-position $80k opportunity is
 * live proof this path is reachable. A deal that is not recognised contributes
 * ZERO DOLLARS; it is never dropped from the list and never given a default.
 *
 * `upsidePct` is expected to have been through `clampUpsidePct` already.
 */
/**
 * quick-261001-nym — the Upside % is now TWO knobs (John, 2026-10-01): one on
 * the Product ARR card for Product Best Case deals, one on the Managed Services
 * card for Managed Best Case deals. A bare number still means "both", so every
 * existing caller (the Monday archive at 50, the tests) is unchanged.
 */
export type UpsidePct = number | { product: number; managed: number };

/** Clamp both knobs; a bare number clamps as before. */
export function normalizeUpside(u: UpsidePct): UpsidePct {
  return typeof u === 'number'
    ? clampUpsidePct(u)
    : { product: clampUpsidePct(u.product), managed: clampUpsidePct(u.managed) };
}

/** The knob that applies to this deal. */
export function upsideFor(d: ForecastDeal, u: UpsidePct): number {
  if (typeof u === 'number') return u;
  return d.sub_group === SUB_GROUP_MANAGED ? u.managed : u.product;
}

export function rate(d: ForecastDeal, upsidePct: UpsidePct): number {
  if (d.tier === 'closed_won' || d.forecast_position === CLOSED_WON_LABEL) return 1;
  if (d.forecast_position === BEST_CASE) return upsideFor(d, upsidePct) / 100;
  if (d.forecast_position === COMMIT || d.forecast_position === MOST_LIKELY) return 1;
  return 0;
}

/**
 * A deal forecast contribution in dollars.
 *
 * `raw_amount`, never `weighted_amount` — the latter is the stored-grid figure
 * baked at recompute time and cannot move with the toggle (RESEARCH Finding 1).
 */
export function fc(d: ForecastDeal, upsidePct: UpsidePct): number {
  return d.raw_amount * rate(d, upsidePct);
}

/** True when the deal banks at 1.00 as already-won revenue. */
function isClosedWon(d: ForecastDeal): boolean {
  return d.tier === 'closed_won' || d.forecast_position === CLOSED_WON_LABEL;
}

/** D-15 tiering for any subset of deals. `total` covers the WHOLE input. */
export interface ForecastSplit {
  /** Closed-Won only, at 1.00. */
  won: number;
  /** Commit + Most Likely at 1.00, Closed-Won excluded (it is in `won`). */
  commit: number;
  /** Best Case at the viewer Upside %. */
  upside: number;
  /**
   * `fc` summed over EVERY deal in the input, so zero-weighted deals are
   * carried at zero rather than dropped. Equals won + commit + upside plus a
   * zero remainder — the remainder is what makes "nothing was dropped"
   * assertable rather than assumed.
   */
  total: number;
}

/**
 * D-15 — won / commit / upside tiering over an arbitrary subset of deals.
 *
 * Pipeline and no-position deals fall through every tier branch and land only
 * in `total`, at zero. That is the point: they stay visible in the count and in
 * the table while contributing no dollars.
 */
export function split(deals: readonly ForecastDeal[], upsidePct: UpsidePct): ForecastSplit {
  let won = 0;
  let commit = 0;
  let upside = 0;
  let total = 0;

  for (const d of deals) {
    const amount = fc(d, upsidePct);
    total += amount;

    if (isClosedWon(d)) {
      won += amount;
    } else if (d.forecast_position === COMMIT || d.forecast_position === MOST_LIKELY) {
      commit += amount;
    } else if (d.forecast_position === BEST_CASE) {
      upside += amount;
    }
    // else: Pipeline / 'No position' / unrecognised — counted in `total` at
    // zero, never dropped (05.1 D-22).
  }

  return { won, commit, upside, total };
}

/** D-13c — the summary strip equation: newBiz + expansion + managed = combinedArr. */
export interface ForecastStreams {
  newBiz: ForecastSplit;
  expansion: ForecastSplit;
  managed: ForecastSplit;
  /**
   * The sum of the three stream totals, computed from the SAME partition that
   * produced them — not a second pass over `deals`. That is what makes the
   * equation hold exactly at any Upside %.
   */
  combinedArr: number;
}

/**
 * D-13c — partition an ARR section deals into the three streams the summary
 * strip reads as an equation.
 *
 * Managed Services is its own stream. The remaining product deals split New
 * versus Expansion where EXPANSION IS PRODUCT THAT IS NOT NEW. This is not a
 * test for an Existing-Business type value: Salesforce returns a blank Type on
 * real records, and a blank Type must land in Expansion rather than falling out
 * of the partition. If it fell out, the three streams would stop summing to
 * Combined ARR and the strip would stop reading as an equation.
 */
export function streams(deals: readonly ForecastDeal[], upsidePct: UpsidePct): ForecastStreams {
  const newDeals: ForecastDeal[] = [];
  const expansionDeals: ForecastDeal[] = [];
  const managedDeals: ForecastDeal[] = [];

  for (const d of deals) {
    if (d.sub_group === SUB_GROUP_MANAGED) {
      managedDeals.push(d);
    } else if (d.sub_group === SUB_GROUP_NEW) {
      newDeals.push(d);
    } else {
      expansionDeals.push(d);
    }
  }

  const newBiz = split(newDeals, upsidePct);
  const expansion = split(expansionDeals, upsidePct);
  const managed = split(managedDeals, upsidePct);

  return {
    newBiz,
    expansion,
    managed,
    combinedArr: newBiz.total + expansion.total + managed.total,
  };
}

// ---------------------------------------------------------------------------
// D-19 — the FY2026 year-to-date model, as the CEO artifact v29 reads it
// (quick-260918-fai): GROSS ONLY. Banked gross plus the as-of quarter's gross
// call, against the gross ARR budgets. No churn, no net, anywhere.
// ---------------------------------------------------------------------------

/**
 * One FY2026 quarter as the year-to-date box reads it.
 *
 * PROMOTED TO `@saledash/shared` BY PLAN 05 — the local declaration this file
 * carried is gone and this is the promised one-line swap. It is a TYPE-ONLY
 * re-export on purpose: a runtime import from the shared top barrel drags the
 * barrel's Node-only graph into the SPA browser bundle and the Rollup build
 * fails (see the module header and `lib/forecast-periods.ts`). `export type`
 * is erased entirely, so nothing from the barrel reaches the bundle.
 *
 * D-20: Q1 and Q2 gross are MANUAL finance-workbook figures seeded as data.
 * They are not derivable from Salesforce — a Closed-Won aggregate is NOT a
 * substitute. `banked: true` is what marks a quarter as final.
 *
 * quick-260918-fai: the contract carries no churn and no net. `gross_budget`
 * is the quarter's gross ARR budget, the same row the period headline reads.
 */
export type { ForecastYtdQuarter };

/**
 * One column of the year-to-date box (quick-260918-fai).
 *
 * Every quarter carries a gross figure and its gross budget. A banked quarter's
 * gross is the seeded figure (null when unseeded — D-20 unresolved, never
 * substituted — and then vsBudget and pct are null with it). An unbanked
 * quarter's gross is the live ARR call at the viewer's Upside % (null when no
 * call is supplied). vsBudget and pct read gross against gross budget on every
 * quarter, banked or not.
 */
export interface GrossYtdRow {
  label: string;
  fiscalQuarter: number;
  banked: boolean;
  /** Seeded gross on a banked quarter; the live ARR call on an open one. */
  gross: number | null;
  /** The quarter's gross ARR budget. */
  grossBudget: number;
  /** gross less grossBudget; null when gross is unresolved. */
  vsBudget: number | null;
  /** Whole percent of vsBudget over grossBudget; null when either is unusable. */
  pct: number | null;
}

/** The year-to-date box: a column per quarter plus the position at the as-of quarter. */
export interface GrossYtdResult {
  rows: GrossYtdRow[];
  /**
   * The fiscal quarter the total is read AT THE END OF: the FIRST unbanked
   * quarter in fiscal order, else the last quarter, else null when empty.
   */
  asOfQuarter: number | null;
  /** Gross over quarters up to and including the as-of quarter. */
  grossTotal: number;
  /** The gross budgets of the same in-scope quarters. */
  grossBudget: number;
  /** grossTotal - grossBudget, the raw over/under D-06 renders beside the percent. */
  delta: number;
  /** Whole percent of grossTotal over grossBudget; null when the budget is zero. */
  pct: number | null;
}

function byFiscalQuarter(a: ForecastYtdQuarter, b: ForecastYtdQuarter): number {
  return a.fiscal_quarter - b.fiscal_quarter;
}

/**
 * D-19 as rewritten by quick-260918-fai — the FY2026 year-to-date position,
 * GROSS ONLY: the banked quarters' gross plus the as-of quarter's gross ARR
 * call, against the sum of the in-scope quarterly GROSS budgets. Quarters AFTER
 * the as-of quarter (Q4 on a Q3 view) are rows only — they never enter the
 * total or the budget.
 *
 * WHY THE ARR CALL IS AN ARGUMENT AND NOT A SERVER FIELD: D-19 requires the
 * total to REFORECAST with the page — moving the Upside % must move it
 * dollar-for-dollar with the ARR call. A value read from the payload would be
 * baked at load and the box would quietly disagree with the equation strip
 * above it. This function is pure and is re-run on every render; it must not be
 * cached, memoised or snapshot (D-03).
 *
 * A banked quarter ignores anything supplied in `arrByQuarterLabel` — its gross
 * is seeded, not derived (D-20). An unbanked as-of quarter with no ARR call
 * carries a null gross and contributes zero rather than throwing or dropping out
 * of the table. Input is sorted by fiscal quarter first so the as-of search is
 * order-independent.
 *
 * quick-260921-f73 — `asOfQuarter` lets the PAGE read the year THROUGH THE
 * QUARTER ON SCREEN (John: "when on a distinct quarter's tab, the YTD number
 * should reflect that quarter"). On the Q4 view the total is Q1..Q4 gross
 * against Q1..Q4 budgets; on the Q3 view Q1..Q3. When omitted, or when no row
 * carries that fiscal quarter, the default rule (first unbanked, else last)
 * applies.
 */
export function grossYtd(
  quarters: readonly ForecastYtdQuarter[],
  arrByQuarterLabel: ReadonlyMap<string, number>,
  asOfQuarter?: number,
): GrossYtdResult {
  const rows: GrossYtdRow[] = [...quarters].sort(byFiscalQuarter).map((q) => {
    const gross = q.banked ? q.growth : (arrByQuarterLabel.get(q.label) ?? null);
    const vsBudget = gross === null ? null : gross - q.gross_budget;
    const pct =
      vsBudget === null || q.gross_budget === 0
        ? null
        : Math.round((vsBudget / q.gross_budget) * 100);
    return {
      label: q.label,
      fiscalQuarter: q.fiscal_quarter,
      banked: q.banked,
      gross,
      grossBudget: q.gross_budget,
      vsBudget,
      pct,
    };
  });

  const defaultAsOf =
    rows.find((r) => !r.banked)?.fiscalQuarter ?? rows.at(-1)?.fiscalQuarter ?? null;
  const asOf =
    asOfQuarter !== undefined && rows.some((r) => r.fiscalQuarter === asOfQuarter)
      ? asOfQuarter
      : defaultAsOf;
  const inScope = asOf === null ? [] : rows.filter((r) => r.fiscalQuarter <= asOf);

  const grossTotal = inScope.reduce((t, r) => t + (r.gross ?? 0), 0);
  const grossBudget = inScope.reduce((t, r) => t + r.grossBudget, 0);

  return {
    rows,
    asOfQuarter: asOf,
    grossTotal,
    grossBudget,
    delta: grossTotal - grossBudget,
    pct: grossBudget === 0 ? null : Math.round((grossTotal / grossBudget) * 100),
  };
}

/**
 * quick-260917-nfy — union the ytd quarter lists of several period payloads by
 * label, FIRST-SEEN WINS, undefined lists skipped, result in fiscal order.
 *
 * WHY: the reader's `ytdQuartersInScope` returns Q1..fq on CQ but Q1..Q4 on NQ
 * and FY (packages/api/src/forecast/reader.ts), and the page already holds all
 * three payloads. Merging them client-side is how Q4 reaches the box on the CQ
 * view with no change to `@saledash/shared` or the API. The page passes the
 * active period's list first so its own quarter objects win the dedupe.
 */
export function mergeYtdQuarters(
  lists: readonly (readonly ForecastYtdQuarter[] | undefined)[],
): ForecastYtdQuarter[] {
  const byLabel = new Map<string, ForecastYtdQuarter>();
  for (const list of lists) {
    if (list === undefined) continue;
    for (const q of list) {
      if (!byLabel.has(q.label)) byLabel.set(q.label, q);
    }
  }
  return [...byLabel.values()].sort(byFiscalQuarter);
}
